import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import ThreeJSSettingsIcon from '@cartwhl/ui/icons/ThreeJSSettingsIcon';

import {
  BonesIcon,
  ChainsIcon,
  CollapseAllIcon,
  ExcludedBoneIcon,
  ExpandAllIcon,
  NamesIcon,
  RolesIcon,
  TransformNodeIcon,
  UnassignedBoneIcon,
} from '../../../presentations/icons';

const OUTLINER_SETTINGS_TRIGGER_CLASS =
  'bg-gray-15/25 text-gray-90 dark:bg-gray-90/25 dark:text-gray-15 hover:bg-gray-15 dark:hover:bg-gray-90 inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg transition-colors';
const OUTLINER_SETTINGS_MENU_WIDTH_PX = 216;
const OUTLINER_SETTINGS_MENU_ITEM_CLASS =
  'dark:bg-gray-90/20 bg-white/20 hover:bg-white/40 dark:hover:bg-gray-90/35 flex w-full items-center gap-2 rounded-lg px-2 py-1 transition-colors';

interface OutlinerSettingsRowButtonProps {
  icon: ReactNode;
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function OutlinerSettingsRowButton({
  icon,
  label,
  active,
  disabled = false,
  onClick,
}: OutlinerSettingsRowButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${OUTLINER_SETTINGS_MENU_ITEM_CLASS} ${
        disabled ? 'cursor-not-allowed opacity-50' : ''
      }`}
      aria-pressed={active}
      aria-label={`${active ? 'Disable' : 'Enable'} ${label}`}
    >
      <span className="text-primary flex h-4 w-4 shrink-0 items-center justify-center">
        {icon}
      </span>
      <Text variant="studio/body/medium/xs" className="text-primary truncate">
        {label}
      </Text>
      <span
        className={`ml-auto h-2.5 w-2.5 shrink-0 rounded-full ${active ? 'bg-blue-50' : 'bg-gray-45 dark:bg-gray-30'}`}
        aria-hidden
      />
    </button>
  );
}

export interface OutlinerSettingsControlProps {
  isOpen: boolean;
  autoRevealSelectedBone: boolean;
  hideExcludedBones: boolean;
  hideUnassignedBones: boolean;
  hideTransformNodes: boolean;
  showExcludedColumn: boolean;
  showRolesColumn: boolean;
  showChainColumn: boolean;
  showTitleRow: boolean;
  columnVisibilityLocked?: boolean;
  onToggleOpen: () => void;
  onClose: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  onToggleHideExcludedBones: () => void;
  onToggleHideUnassignedBones: () => void;
  onToggleHideTransformNodes: () => void;
  onToggleShowExcludedColumn: () => void;
  onToggleShowRolesColumn: () => void;
  onToggleShowChainColumn: () => void;
  onToggleShowTitleRow: () => void;
  onToggleAutoRevealSelectedBone: () => void;
}

export default function OutlinerSettingsControl({
  isOpen,
  autoRevealSelectedBone,
  hideExcludedBones,
  hideUnassignedBones,
  hideTransformNodes,
  showExcludedColumn,
  showRolesColumn,
  showChainColumn,
  showTitleRow,
  columnVisibilityLocked = false,
  onToggleOpen,
  onClose,
  onExpandAll,
  onCollapseAll,
  onToggleHideExcludedBones,
  onToggleHideUnassignedBones,
  onToggleHideTransformNodes,
  onToggleShowExcludedColumn,
  onToggleShowRolesColumn,
  onToggleShowChainColumn,
  onToggleShowTitleRow,
  onToggleAutoRevealSelectedBone,
}: OutlinerSettingsControlProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (containerRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen, onClose]);

  return (
    <div className="flex flex-col items-start gap-1.5">
      <Tooltip text="Expand all outliner bones" side="top" inModal>
        <button
          type="button"
          onClick={onExpandAll}
          className={OUTLINER_SETTINGS_TRIGGER_CLASS}
          aria-label="Expand all outliner bones"
        >
          <ExpandAllIcon className="h-4 w-4" />
        </button>
      </Tooltip>
      <Tooltip text="Collapse all outliner bones" side="top" inModal>
        <button
          type="button"
          onClick={onCollapseAll}
          className={OUTLINER_SETTINGS_TRIGGER_CLASS}
          aria-label="Collapse all outliner bones"
        >
          <CollapseAllIcon className="h-4 w-4" />
        </button>
      </Tooltip>
      <div ref={containerRef} className="relative">
        <Tooltip
          text={
            isOpen
              ? 'Collapse outliner settings menu'
              : 'Expand outliner settings menu'
          }
          side="top"
          inModal
        >
          <button
            type="button"
            onClick={onToggleOpen}
            className={OUTLINER_SETTINGS_TRIGGER_CLASS}
            aria-expanded={isOpen}
            aria-label="Toggle outliner settings menu"
          >
            <ThreeJSSettingsIcon className="h-4 w-4" />
          </button>
        </Tooltip>
        <div
          className={`absolute bottom-0 left-full ml-1.5 overflow-hidden transition-all duration-200 ease-out ${isOpen ? 'pointer-events-auto max-w-[240px] translate-x-0 opacity-100' : 'pointer-events-none max-w-0 -translate-x-2 opacity-0'}`}
        >
          <div
            className="dark:bg-gray-90/40 bg-gray-15/45 rounded-xl p-1 backdrop-blur-md"
            style={{ width: OUTLINER_SETTINGS_MENU_WIDTH_PX }}
          >
            <div className="flex flex-col gap-1">
              <OutlinerSettingsRowButton
                icon={<ExcludedBoneIcon className="h-3.5 w-3.5" />}
                label="Filter Excluded Bones"
                active={hideExcludedBones}
                onClick={onToggleHideExcludedBones}
              />
              <OutlinerSettingsRowButton
                icon={<UnassignedBoneIcon className="h-3.5 w-3.5" />}
                label="Filter Unassigned Bones"
                active={hideUnassignedBones}
                onClick={onToggleHideUnassignedBones}
              />
              <OutlinerSettingsRowButton
                icon={<TransformNodeIcon className="h-3.5 w-3.5" />}
                label="Filter Transform Nodes"
                active={hideTransformNodes}
                onClick={onToggleHideTransformNodes}
              />
              <OutlinerSettingsRowButton
                icon={<ExcludedBoneIcon className="h-3.5 w-3.5" />}
                label="Show Excluded Column"
                active={showExcludedColumn}
                onClick={onToggleShowExcludedColumn}
              />
              <OutlinerSettingsRowButton
                icon={<RolesIcon className="h-3.5 w-3.5" />}
                label="Show Roles"
                active={showRolesColumn}
                disabled={columnVisibilityLocked}
                onClick={onToggleShowRolesColumn}
              />
              <OutlinerSettingsRowButton
                icon={<ChainsIcon className="h-3.5 w-3.5" />}
                label="Show Chains"
                active={showChainColumn}
                disabled={columnVisibilityLocked}
                onClick={onToggleShowChainColumn}
              />
              <OutlinerSettingsRowButton
                icon={<NamesIcon className="h-3.5 w-3.5" />}
                label="Show Column Titles"
                active={showTitleRow}
                onClick={onToggleShowTitleRow}
              />
              <OutlinerSettingsRowButton
                icon={<BonesIcon className="h-3.5 w-3.5" />}
                label="Auto Reveal Selected Bone"
                active={autoRevealSelectedBone}
                onClick={onToggleAutoRevealSelectedBone}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
